<template>
  <div class="state-fact-sheet" :class="{ 'is-loading': loading }">
    <div
      class="state-fact-sheet__frame"
      :class="{ 'is-clickable': currentDownloadUrl }"
      :style="{ height: frameHeight }"
      :role="currentDownloadUrl ? 'link' : null"
      :tabindex="currentDownloadUrl ? 0 : null"
      :aria-label="currentDownloadUrl ? downloadLabel : null"
      @click="open"
      @keydown.enter="open"
      ref="frame"
    >
      <div class="state-fact-sheet__scaler" :style="{ transform: 'scale(' + scale + ')' }">
        <div v-if="html" v-html="html"></div>
        <slot v-else></slot>
      </div>
    </div>
  </div>
</template>

<script>
var SHEET_WIDTH = 816;
var SHEET_HEIGHT = 1056;
var MAX_FIT = 3;

export default {
  name: "StateFactSheet",
  props: {
    stateCode: { type: String, default: "" },
    downloadUrl: { type: String, default: "" },
    downloadLabel: { type: String, default: "View the fact sheet" },
  },
  data: function () {
    return {
      scale: 1,
      observer: null,
      html: null,
      currentDownloadUrl: this.downloadUrl,
      loading: false,
      requestId: 0,
    };
  },
  computed: {
    frameHeight: function () {
      return Math.round(SHEET_HEIGHT * this.scale) + "px";
    },
  },
  methods: {
    open: function () {
      if (this.currentDownloadUrl) {
        window.open(this.currentDownloadUrl, "_blank", "noopener,noreferrer");
      }
    },
    // Each card row is its own table, so longer copy only stretches its own row. A sheet should
    // have one card size (the PDF renderer does the same): give every card the tallest one's
    // height. Cards start from the height the server rendered, so it re-measures cleanly.
    equalizeCards: function () {
      var cards = this.$el.querySelectorAll(".sheet__card");
      var i, style, height;
      var max = 0;

      for (i = 0; i < cards.length; i++) {
        if (cards[i].dataset.baseHeight === undefined) {
          cards[i].dataset.baseHeight = cards[i].style.height;
        }
        cards[i].style.height = cards[i].dataset.baseHeight;
      }
      for (i = 0; i < cards.length; i++) {
        style = window.getComputedStyle(cards[i]);
        height =
          cards[i].offsetHeight -
          parseFloat(style.paddingTop) -
          parseFloat(style.paddingBottom) -
          parseFloat(style.borderTopWidth) -
          parseFloat(style.borderBottomWidth);
        if (height > max) max = height;
      }
      for (i = 0; i < cards.length; i++) {
        cards[i].style.height = max + "px";
      }
    },
    // Lays the sheet out: equal cards, then, if long intro copy (editable in the CMS) pushes the
    // last card row past .sheet__fit-limit, tighter fit steps (data-fit, see the SCSS) one at a
    // time until it clears. Mirrors what the PDF renderer does with its fit candidates.
    layout: function () {
      var page = this.$el.querySelector(".state-fact-sheet__page");
      var limit = page && page.querySelector(".sheet__fit-limit");
      var fit = 0;

      var body = page && page.querySelector(".sheet__body");
      var top, bottom;

      if (!page) return;
      page.removeAttribute("data-fit");
      if (body) body.style.transform = "";
      this.equalizeCards();
      if (!limit) return;
      while (fit < MAX_FIT && this.cardsBottom(page) > limit.getBoundingClientRect().top) {
        fit++;
        page.setAttribute("data-fit", fit);
        this.equalizeCards();
      }

      // Copy far beyond what the steps absorb: shrink the whole body to fit rather than run the
      // cards into the footer. (The PDF's type is smaller, so its own steps go further.)
      bottom = this.cardsBottom(page);
      if (body && bottom > limit.getBoundingClientRect().top) {
        top = body.getBoundingClientRect().top;
        body.style.transformOrigin = "top center";
        body.style.transform = "scale(" + (limit.getBoundingClientRect().top - top) / (bottom - top) + ")";
      }
    },
    cardsBottom: function (page) {
      var cards = page.querySelectorAll(".sheet__card");
      var bottom = 0;
      for (var i = 0; i < cards.length; i++) {
        bottom = Math.max(bottom, cards[i].getBoundingClientRect().bottom);
      }
      return bottom;
    },
    resize: function () {
      var frame = this.$refs.frame;
      if (!frame) return;
      var width = frame.clientWidth;
      if (width > 0) this.scale = width / SHEET_WIDTH;
    },
    onSelect: function (event) {
      var detail = event.detail || {};
      if (detail.stateCode !== this.stateCode) return;
      this.currentDownloadUrl = detail.downloadUrl || this.downloadUrl;
      this.load(detail.sheetUrl);
    },
    load: function (url) {
      if (!url) {
        this.html = null;
        return;
      }
      var self = this;
      var request = ++this.requestId;
      this.loading = true;

      fetch(url, { headers: { Accept: "text/html" } })
        .then(function (response) {
          return response.ok ? response.text() : Promise.reject(response.status);
        })
        .then(function (html) {
          if (request !== self.requestId) return;
          self.html = html;
        })
        .catch(function () {})
        .finally(function () {
          if (request === self.requestId) self.loading = false;
        });
    },
  },
  watch: {
    html: function () {
      this.$nextTick(this.layout);
    },
  },
  mounted: function () {
    this.resize();
    this.layout();
    // Card copy rewraps once the web fonts arrive. fonts.ready alone isn't enough: on a cold load
    // it can settle before the sheet's fonts have even started loading.
    if (document.fonts) {
      if (document.fonts.ready) document.fonts.ready.then(this.layout);
      document.fonts.addEventListener("loadingdone", this.layout);
    }
    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(this.resize);
      this.observer.observe(this.$refs.frame);
    } else {
      window.addEventListener("resize", this.resize);
    }
    window.addEventListener("state-fact-sheet:select", this.onSelect);
  },
  beforeDestroy: function () {
    if (this.observer) this.observer.disconnect();
    else window.removeEventListener("resize", this.resize);
    window.removeEventListener("state-fact-sheet:select", this.onSelect);
    if (document.fonts) document.fonts.removeEventListener("loadingdone", this.layout);
  },
};
</script>
